import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./accountSync.module.css";
import { getAccountSyncBatch, confirmAccountSync, pairAccountSyncTransactions } from "../../api/client";

const STATES = {
  pending: "待确认", running: "正在同步", complete: "同步完成", stopped: "同步已中止",
  stale: "清单已变化", blocked: "需要人工核对", resolved: "差异已消除",
};
const REASONS = {
  target_has_independent_or_edited_transactions: "目标存在独有或被修改的交易",
  shared_transaction_order_differs: "两边交易基线或顺序不同",
  source_row_is_revision_not_new_trade: "来源交易曾被更正",
  ambiguous_duplicate_trade: "存在无法区分的相同交易",
  missing_or_reversed_source_audit: "来源操作证据不完整或已撤销",
  opening_position_differs: "录入持仓不同",
  opening_position_requires_manual_review: "含录入持仓，需要人工核对",
  new_holding_requires_manual_review: "新增标的含录入持仓或状态需要核对",
  holding_not_active: "持仓不是在持状态",
  target_holding_missing: "目标尚无此标的",
  target_has_other_holding_lifecycle: "目标已有其他生命周期的持仓",
  target_only_holding: "仅目标拥有此标的",
  source_price_confirmation_requires_manual_review: "来源交易含异常价确认，需要人工核对",
  source_columns_and_facts_disagree: "来源事实字段不一致",
  source_asset_identity_disagrees: "来源标的身份不一致",
  both_changed: "来源和目标都已修改同一交易",
  target_changed: "目标交易已独立修改，保留目标内容",
  target_deleted: "目标交易已独立删除，保留目标操作",
  source_update_requires_price_confirmation: "来源修改包含异常价确认，需人工处理",
  baseline_source_only_history: "来源已有交易尚未建立对应关系，请核对",
  baseline_target_only_history: "目标有尚未对应的交易，已保留；请核对两边是否为同一笔",
  new_source_matches_unpaired_target: "来源新交易与目标未配对交易相似，需人工配对",
  source_transaction_not_after_baseline: "来源交易缺少基线之后的新增证明",
  target_evidence_invalid: "目标交易事实或审计异常，需人工核对",
  source_evidence_changed: "来源证据变化，不能作为普通金额修改同步",
  source_holding_missing: "来源持仓缺失，不能推断交易被删除",
  source_history_missing: "来源交易历史缺失，不能推断删除",
  sync_result_unresolved: "此前同步结果尚未核实，此笔暂停同步，避免重复记账；其他可选交易不受影响",
  asset_type_differs: "标的类型不同", unsupported_action: "交易类型需要人工处理",
};

export function AccountSyncBoundary({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isReview = location.pathname.startsWith("/account-sync/");
  const batchId = location.pathname.split("/")[2];
  const accessToken = new URLSearchParams(location.hash.slice(1)).get("access") || "";
  if (isReview) return <AccountSync key={`${batchId}:${accessToken}`} batchId={batchId} accessToken={accessToken} onBack={() => navigate("/overview")} />;
  return children;
}

function AccountSync({ batchId, accessToken, onBack }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);
  const [selected, setSelected] = React.useState([]);
  const [pairChoices, setPairChoices] = React.useState({});
  const notices = (data?.conflicts || []).filter((row) => ["preserved", "excluded"].includes(row.disposition));
  const reviews = (data?.conflicts || []).filter((row) => !["preserved", "excluded"].includes(row.disposition));
  React.useEffect(() => {
    let active = true;
    let timer;
    if (!accessToken) return undefined;
    async function load() {
      try {
        const next = await getAccountSyncBatch(batchId, accessToken);
        if (!active) return;
        setData(next);
        if (next.state === "running") timer = setTimeout(load, 2000);
      } catch (e) { if (active) setError(e.payload?.error?.message || e.message); }
    }
    void load();
    return () => { active = false; clearTimeout(timer); };
  }, [batchId, accessToken, refresh]);

  async function confirm() {
    if (submitting || data?.state !== "pending" || !selected.length) return;
    setSubmitting(true);
    setError("");
    try {
      const next = await confirmAccountSync(batchId, data.confirmation_token, selected, accessToken);
      setData(next);
    } catch (e) {
      setError(`${e.payload?.error?.message || e.message} 如请求中断，请查看执行状态，不要重复提交。`);
    } finally { setSubmitting(false); setRefresh((n) => n + 1); }
  }

  async function pair(assetCode) {
    const choice = pairChoices[assetCode];
    if (!choice?.source || !choice?.target || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const next = await pairAccountSyncTransactions(batchId, {
        asset_code: assetCode, source_transaction_id: choice.source, target_transaction_id: choice.target,
      }, accessToken);
      setData(next);
      setPairChoices({});
      setSelected([]);
    } catch (e) { setError(e.payload?.error?.message || e.message); }
    finally { setSubmitting(false); }
  }

  return <main className={styles.page} id="main-content">
    <section className={styles.panel} aria-labelledby="sync-title">
      <header className={styles.header}>
        <div><p className={styles.eyebrow}>账号维护</p><h1 id="sync-title">交易同步</h1></div>
        <button type="button" onClick={onBack}>返回应用</button>
      </header>
      {!accessToken ? <p role="alert">此链接缺少访问凭证，请从最新企微消息重新打开。</p>
        : <>
          {error && <p className={styles.error} role="alert">{error}</p>}
          {!data && !error && <p role="status">正在读取待补交易…</p>}
          {data && <>
            <p className={styles.direction}>{data.source} <span aria-label="同步至">→</span> {data.target}</p>
            <div className={styles.summary}><strong>{data.state === "blocked" && reviews.length === 0 ? "没有待同步交易" : STATES[data.state] || "状态待核验"}</strong>
              <span>发现于 {new Date(data.created_at).toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", hour12: false })}（北京时间）</span></div>
            <h2>本批交易 · {data.transactions.length} 笔</h2>
            {data.state === "pending" && <p>请勾选本次需要同步的交易。未勾选的交易保持原样。</p>}
            <div className={styles.tableWrap}><table><thead><tr>
              <th>选择</th><th>操作</th><th>标的</th><th>交易日期</th><th>方向</th><th>数量</th><th>成交价</th><th>实际费用</th>
            </tr></thead><tbody>{data.transactions.map((row) => <tr key={row.id}>
              <td>{data.state === "pending" ? <input type="checkbox"
                aria-label={`${({ add: "新增", update: "修改", delete: "删除" })[row.operation] || "同步"} ${row.asset_name || row.asset_code} ${row.trade_date || row.before?.trade_date} ${row.quantity ?? row.before?.quantity}`}
                checked={selected.includes(row.id)} disabled={submitting}
                onChange={(event) => setSelected((old) => event.target.checked ? [...old, row.id] : old.filter((id) => id !== row.id))} />
                : data.selected_candidate_ids == null ? "历史批次" : data.selected_candidate_ids.includes(row.id) ? "已选择" : "未选择"}</td>
              <td>{({ add: "新增", update: "修改", delete: "删除" })[row.operation] || "变更"}</td>
              <td><strong>{row.asset_name || row.asset_code}</strong><small>{row.asset_code}{row.new_holding ? " · 新增标的" : ""}</small></td>
              <td>{row.trade_date ?? row.before?.trade_date ?? "—"}{row.before && <small>原 {row.before.trade_date}</small>}</td>
              <td>{(row.action || row.before?.action) === "BUY" ? "买入" : (row.action || row.before?.action) === "SELL" ? "卖出" : "—"}{row.before && <small>原 {row.before.action === "BUY" ? "买入" : "卖出"}</small>}</td>
              <td>{row.quantity ?? row.before?.quantity ?? "—"}{row.before && <small>原 {row.before.quantity}</small>}</td>
              <td>{row.price ?? row.before?.price ?? "—"}{row.before && <small>原 {row.before.price}</small>}</td>
              <td>{row.actual_fee ?? row.fee ?? row.before?.actual_fee ?? row.before?.fee ?? "—"}{row.before && <small>原 {row.before.actual_fee ?? row.before.fee ?? "未提供"}</small>}</td>
            </tr>)}</tbody></table></div>
            {notices.length > 0 && <section className={styles.conflicts}>
              <h2>已保留或不参与同步 · {notices.length} 项</h2>
              <p>以下记录无需操作，不会自动修改或删除。</p>
              {notices.map((row, index) => <div key={`notice-${row.asset_code}-${index}`}>
                <h3>{row.asset_name || row.target_transaction?.asset_name || row.asset_code}{(row.asset_name || row.target_transaction?.asset_name) ? ` · ${row.asset_code}` : ""}</h3>
                <p>{row.disposition === "preserved" ? "目标账户独有的历史交易，已保留，无需操作。"
                  : "来源和目标持仓均已作废，有效交易记录一致，不参与同步，无需操作。"}</p>
                {row.target_transaction && <p>目标交易：{row.target_transaction.trade_date} · {row.target_transaction.action === "BUY" ? "买入" : "卖出"} · 数量 {row.target_transaction.quantity} · 价格 {row.target_transaction.price} · 费用 {row.target_transaction.actual_fee ?? row.target_transaction.fee ?? "未提供"}</p>}
              </div>)}
            </section>}
            {reviews.length > 0 && <section className={styles.conflicts}>
              <h2>需人工核对 · {reviews.length} 项</h2>
              <p>以下差异不会自动同步。</p>
              {reviews.map((conflict, index) => <div key={`${conflict.asset_code}-${index}`}>
                <h3>{conflict.asset_code}</h3><p>{conflict.reasons.map((reason) => REASONS[reason] || "数据差异需要人工核对").join("；")}</p>
                {conflict.source_transaction && <p>来源交易：{conflict.source_transaction.trade_date} · {conflict.source_transaction.action === "BUY" ? "买入" : "卖出"} · 数量 {conflict.source_transaction.quantity} · 价格 {conflict.source_transaction.price} · 费用 {conflict.source_transaction.actual_fee ?? conflict.source_transaction.fee ?? "未提供"}</p>}
                {conflict.target_transaction && <p>目标交易：{conflict.target_transaction.trade_date} · {conflict.target_transaction.action === "BUY" ? "买入" : "卖出"} · 数量 {conflict.target_transaction.quantity} · 价格 {conflict.target_transaction.price} · 费用 {conflict.target_transaction.actual_fee ?? conflict.target_transaction.fee ?? "未提供"}</p>}
                {(conflict.source_only || []).map((trade, i) => <p key={i}>来源独有：{trade[0]} · {trade[1] === "BUY" ? "买入" : "卖出"} · 数量 {trade[2]} · 价格 {trade[3]} · 费用 {trade[4] ?? "未提供"}</p>)}
              </div>)}
              {["pending", "blocked"].includes(data.state) && (() => {
                const sourceRows = reviews.filter((row) => row.kind === "unpaired_history" && row.source_transaction_id && row.source_transaction);
                const targetRows = reviews.filter((row) => row.kind === "unpaired_history" && row.target_transaction_id && row.target_transaction);
                const assets = [...new Set(sourceRows.map((row) => row.asset_code).filter((asset) => targetRows.some((row) => row.asset_code === asset)))];
                return assets.map((asset) => {
                  const sources = sourceRows.filter((row) => row.asset_code === asset);
                  const targets = targetRows.filter((row) => row.asset_code === asset);
                  const choice = pairChoices[asset] || {};
                  return <div key={`pair-${asset}`} className={styles.pairing}>
                    <strong>明确配对 {asset}</strong>
                    <p>只有确认两笔代表同一笔交易时才配对。配对本身不改账；事实不同会生成待选修改，需再确认同步。</p>
                    <select aria-label={`${asset} 来源交易`} value={choice.source || ""} onChange={(event) => setPairChoices((old) => ({ ...old, [asset]: { ...old[asset], source: event.target.value } }))}>
                      <option value="">选择来源交易（{data.source}）</option>{sources.map((row) => <option key={row.source_transaction_id} value={row.source_transaction_id}>{row.source_transaction.trade_date} · {row.source_transaction.action} · {row.source_transaction.quantity} @ {row.source_transaction.price}</option>)}
                    </select>
                    <select aria-label={`${asset} 目标交易`} value={choice.target || ""} onChange={(event) => setPairChoices((old) => ({ ...old, [asset]: { ...old[asset], target: event.target.value } }))}>
                      <option value="">选择目标交易（{data.target}）</option>{targets.map((row) => <option key={row.target_transaction_id} value={row.target_transaction_id}>{row.target_transaction.trade_date} · {row.target_transaction.action} · {row.target_transaction.quantity} @ {row.target_transaction.price}</option>)}
                    </select>
                    <button type="button" disabled={submitting || !choice.source || !choice.target} onClick={() => pair(asset)}>确认配对</button>
                  </div>;
                });
              })()}
            </section>}
            {data.result?.message && <section className={styles.result} role="status">
              <h2>{STATES[data.state]}</h2><p>{data.result.message}</p>
              {(data.result.receipts || []).map((receipt, i) => <p key={i}>{receipt.asset_code}：{
                receipt.state === "not_submitted" ? "未执行" : receipt.http_status === 200 ? receipt.calculation_status === "success" ? "已保存，重算成功" : "已保存，核算未成功"
                  : [401, 403, 409, 422].includes(receipt.http_status) ? "提交未通过" : "结果待核验"
              }（新增 {receipt.add_count || 0}、修改 {receipt.update_count || 0}、删除 {receipt.delete_count || 0}）</p>)}
            </section>}
            {data.state === "pending" && <footer className={styles.actions}>
              <p>已选 {selected.length} 笔。确认后仅执行所选新增、修改或删除，并重新核算。</p>
              <button className={styles.confirm} type="button" disabled={submitting || !selected.length} onClick={confirm}>{submitting ? "正在提交…" : `确认同步所选 ${selected.length} 笔`}</button>
            </footer>}
            {data.state === "running" && <p role="status">正在同步和重算，请稍候。此页面会自动更新执行结果。</p>}
            {data.state === "stale" && <p role="status">来源或目标已变化，本清单不可再确认。请打开最新提醒核对。</p>}
          </>}
        </>}
    </section>
  </main>;
}
